import { useState } from "react";
import { ClipboardList, ShoppingCart, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { planningRows } from "@/lib/planning";
import { loc } from "@/lib/numerals";
import { PlanningRow } from "@/components/planning/PlanningRow";

const HEADERS = ["Product", "Pending Demand", "Reserved", "Available to Plan", "In Production", "Need to Produce", "Action"];

// Production Planning report: demand from open sales orders minus stock and WIP => "Need to Produce".
export default function ProductionPlanning({ salesOrders = [], bomItems = [], orders = [], canCreate = true, onCreateOrder, onOrderAllShort }) {
  const [ordering, setOrdering] = useState(false);
  const runOrderAllShort = async () => {
    if (!onOrderAllShort) return;
    setOrdering(true);
    try { await onOrderAllShort(); } finally { setOrdering(false); }
  };
  const rows = planningRows(salesOrders, bomItems, orders);
  const totalToProduce = rows.reduce((s, r) => s + r.need_to_produce, 0);

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="production-planning">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
        <ClipboardList className="h-4 w-4 text-blue-600" />
        <h3 className="font-display font-semibold text-slate-800">Production Planning</h3>
        <span className="text-xs text-slate-400">demand from open sales orders (Pending + Confirmed) vs. warehouse stock</span>
        <span className="ml-auto text-xs text-slate-500">Total to produce: <span className="font-bold text-blue-600" data-testid="planning-total">{loc(totalToProduce.toLocaleString("en-IN"))}</span></span>
        {canCreate && onOrderAllShort && rows.length > 0 && (
          <Button size="sm" className="h-7 text-[11px] bg-red-600 hover:bg-red-700 ml-2" disabled={ordering} onClick={runOrderAllShort} data-testid="planning-order-all-short">
            {ordering ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <ShoppingCart className="h-3 w-3 mr-1" />}Order everything short
          </Button>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[820px] text-xs">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-400">
            <tr>{HEADERS.map((h, i) => <th key={h} className={`${i === 0 ? "text-left" : "text-right"} font-semibold px-4 py-2`}>{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400" data-testid="planning-empty">All demand is covered — nothing to produce right now.</td></tr>}
            {rows.map((r) => <PlanningRow key={r.id} r={r} canCreate={canCreate} onCreateOrder={onCreateOrder} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}
